import type {
  BlockingElement,
  GuidedCanvasSummary,
  GuidedElementRow,
} from '@/services/guided-use-case-service';

type BadgeVariant = 'success' | 'warning' | 'default' | 'destructive';

export interface StatusDescriptor {
  label: string;
  variant: BadgeVariant;
}

/**
 * @param {GuidedCanvasSummary} canvas - the canvas coverage summary from the API
 * @returns {StatusDescriptor} the label and badge tone the canvases list shows
 */
export function canvasStatus(canvas: GuidedCanvasSummary): StatusDescriptor {
  if (canvas.published) return { label: 'Live', variant: 'success' };
  if (canvas.publishable) return { label: 'Ready to publish', variant: 'warning' };
  if (canvas.authored_count === 0) return { label: 'Not started', variant: 'default' };

  const blocking = canvas.blocking_elements;
  const undefinedElements = blocking.filter((element) => !element.reason);
  const label =
    undefinedElements.length === blocking.length
      ? `${blocking.length} ${blocking.length === 1 ? 'element' : 'elements'} missing`
      : `${blocking.length} ${blocking.length === 1 ? 'element' : 'elements'} blocking`;

  return { label, variant: 'warning' };
}

/**
 * @param {BlockingElement[]} blocking - what the API says is stopping publication
 * @returns {string | null} a sentence naming each blocker, or null when there are none
 */
export function blockerSummary(blocking: BlockingElement[]): string | null {
  if (blocking.length === 0) return null;

  const undefinedElements = blocking.filter((element) => !element.reason);
  const sentences = blocking
    .filter((element) => element.reason)
    .map((element) => `${elementName(element)}: ${element.reason}.`);

  if (undefinedElements.length > 0) {
    const named = undefinedElements.map(elementName).join(', ');
    sentences.unshift(
      `${named} ${undefinedElements.length === 1 ? 'has' : 'have'} no definition of good yet.`
    );
  }

  return sentences.join(' ');
}

/**
 * @param {BlockingElement[]} blocking - what the API says is stopping publication
 * @returns {string | null} a counted one-liner for the canvases table, or null when nothing blocks
 */
export function blockerHeadline(blocking: BlockingElement[]): string | null {
  if (blocking.length === 0) return null;

  const undefinedCount = blocking.filter((element) => !element.reason).length;
  const ungradedCount = blocking.length - undefinedCount;
  const parts: string[] = [];

  if (undefinedCount > 0) {
    parts.push(
      `${undefinedCount} ${undefinedCount === 1 ? 'element has' : 'elements have'} no definition of good yet`
    );
  }
  if (ungradedCount > 0) {
    parts.push(
      `${ungradedCount} ${ungradedCount === 1 ? 'step has' : 'steps have'} nothing to grade against`
    );
  }

  return `${parts.join('; ')}.`;
}

function elementName(element: BlockingElement): string {
  return element.title ?? element.slot;
}

/**
 * @param {Pick<GuidedElementRow, 'authored' | 'source'>} element - the element row from the API
 * @returns {StatusDescriptor} the pill label and tone the element list shows
 */
export function elementStatus(
  element: Pick<GuidedElementRow, 'authored' | 'source'>
): StatusDescriptor {
  if (element.source === 'derived') return { label: 'Computed', variant: 'default' };

  return element.authored
    ? { label: 'Defined', variant: 'success' }
    : { label: 'Not started', variant: 'default' };
}

/**
 * @param {GuidedCanvasSummary} canvas - the canvas coverage summary from the API
 * @returns {string} the class that recolours the Progress indicator for this state
 */
export function coverageBarClass(canvas: GuidedCanvasSummary): string {
  if (canvas.authored_count === 0) {
    return '[&_[data-slot=progress-indicator]]:bg-neutral-300';
  }
  return canvas.publishable
    ? '[&_[data-slot=progress-indicator]]:bg-success'
    : '[&_[data-slot=progress-indicator]]:bg-warning';
}

/**
 * @param {GuidedCanvasSummary} canvas - the canvas coverage summary from the API
 * @returns {number} coverage as a percentage, 0 when the canvas has no elements
 */
export function coveragePercent(canvas: GuidedCanvasSummary): number {
  if (canvas.element_count === 0) return 0;
  return (canvas.authored_count / canvas.element_count) * 100;
}
